import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe, registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { FaConfig } from '@fortawesome/angular-fontawesome';
import { provideNgxHateoasClient } from '@lagoshny/ngx-hateoas-client';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import {
  AutoRefreshTokenService,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
  UserActivityService,
} from 'keycloak-angular';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { Company } from './models/company/company-models.model';
import { FileDocument } from './models/company/document.model';
import { ElementCode } from './models/element/element-code.model';
import { ElementGroupCode } from './models/element/element-group-code.model';
import { InspectionListItemCode } from './models/inspection/inspection-list-item-code.model';
import { InspectionMethodCode } from './models/inspection/inspection-method-code.model';
import { DocumentCode } from './models/labels/document-code.model';
import { LayerFeaturesCode } from './models/labels/layer-features-code.model';
import { ObjectCategoryCode } from './models/labels/object-category-code.model';
import { RgsObject } from './models/objects/rgs-object.model';
import { PricebookCode } from './models/pricebook/pricebook-code.model';
import { Scenario } from './models/scenario/scenario.model';
import { StructureCode } from './models/structure/structure-code.model';
import { UserProfile } from './models/userprofile/user-profile.model';
import { rgsPreset } from './rgs-preset';
import { InspectionItemCode } from './rgs/components/objects/object-item/object-inspection/models/inspection-item-code.model';
import { Inspection } from './rgs/components/objects/object-item/object-inspection/models/inspection.model';
import { Note } from './rgs/components/objects/object-item/object-inspection/models/note.model';
import { I18nVersionService } from './services/i18n-version/i18n-version.service';
import { VersionedTranslateLoader } from './services/i18n-version/versioned-translate.loader';
import { licenseInterceptor } from './shared/interceptors/license.interceptor';
import { sessionInterceptor } from './shared/interceptors/session.interceptor';
import { CapitalizePipe } from './shared/pipes/capitalize.pipe';
import { UserInfoPipe } from './shared/pipes/user-info.pipe';
import { ErrorHandlerService } from './shared/services/error-handler.service';
import { DATE_FORMAT } from './shared/utils/local-date.utils';

registerLocaleData(localeNl, 'nl');

export function httpTranslateLoader(http: HttpClient, i18nVersionService: I18nVersionService) {
  return new VersionedTranslateLoader(http, i18nVersionService);
}

export function initializeI18nVersion() {
  return inject(I18nVersionService).load();
}

export const appConfig: ApplicationConfig = {
  providers: [
    // --- Angular core -------------------------------------------------------
    provideZonelessChangeDetection(),
    provideRouter(routes, withInMemoryScrolling({ anchorScrolling: 'enabled' })),
    provideHttpClient(
      withXhr(),
      withInterceptors([includeBearerTokenInterceptor, licenseInterceptor, sessionInterceptor]),
    ),
    // --- Authentication -----------------------------------------------------
    provideKeycloak({
      config: environment.keycloak_config,
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      },
      providers: [
        AutoRefreshTokenService,
        UserActivityService,
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [
            createInterceptorCondition<IncludeBearerTokenCondition>({
              urlPattern: /^(?!.*maps\.googleapis\.com).*/,
            }),
          ],
        },
      ],
    }),

    // --- Data access --------------------------------------------------------
    provideNgxHateoasClient({
      http: {
        rootUrl: environment.springAppUrl,
      },
      useTypes: {
        resources: [
          RgsObject,
          StructureCode,
          UserProfile,
          PricebookCode,
          ElementGroupCode,
          ElementCode,
          DocumentCode,
          LayerFeaturesCode,
          ObjectCategoryCode,
          Inspection,
          InspectionListItemCode,
          InspectionMethodCode,
          InspectionItemCode,
          Note,
          Scenario,
          FileDocument,
          Company,
        ],
      },
    }),

    // --- i18n and locale ----------------------------------------------------
    provideAppInitializer(initializeI18nVersion),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient, I18nVersionService],
      },
    }),
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: DATE_FORMAT } },

    // --- UI library ---------------------------------------------------------
    providePrimeNG({
      license: environment.primengKey,
      ripple: true,
      theme: {
        preset: rgsPreset,
        options: {
          darkModeSelector: 'none',
        },
      },
    }),
    MessageService,
    ConfirmationService,
    DialogService,
    {
      provide: FaConfig,
      useValue: {
        fixedWidth: true,
      },
    },

    // --- Application --------------------------------------------------------
    DatePipe,
    CapitalizePipe,
    UserInfoPipe,
    { provide: ErrorHandler, useClass: ErrorHandlerService },
  ],
};
